import { Box, Spinner, Text, TextButton } from '@zillow/constellation-10';
import { IconExternalOutline } from '@zillow/constellation-icons';

import { useRentalManagerGrowthGetCompsForListingWebApiHandler_getComps } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';
import { StaticMap } from '@zg-rentals/zillow-rental-manager';
import { formatAddress } from '@zg-rentals/zillow-rental-manager/utils';

import { useScreenSize } from '../../../hooks/useScreenSize';

import { useAuth } from '../../AuthProvider';

import type { GetUserPropertyOverviewAddressWebDto } from '@zg-rentals/ts-rental-manager-api-client';

const PMR_LANDING_PAGE = 'https://www.zillow.com/rental-manager/price-my-rental/';
const PMR_RESULTS_BASE = 'https://www.zillow.com/rental-manager/price-my-rental/results/';

type Props = {
  listingAlias: string;
  address: GetUserPropertyOverviewAddressWebDto | undefined;
  hasAnalyticsRentComps?: boolean;
};

const Comps = ({ listingAlias, address, hasAnalyticsRentComps = false }: Props) => {
  const { user } = useAuth();
  const { isScreenTablet, isScreenLgGTE } = useScreenSize();

  const { data, isLoading, error } = useRentalManagerGrowthGetCompsForListingWebApiHandler_getComps(
    {
      listingAlias,
    },
    {
      enabled: Boolean(listingAlias),
      meta: {
        tags: ['listingComps'],
        context: { api: 'rentalManagerGrowth', details: 'Comps' },
      },
    },
  );

  if (isLoading) {
    return (
      <Box css={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
        <Spinner size="sm" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Text textStyle="body-sm">{DatarouterError.getDisplayMessage(error)}</Text>
      </Box>
    );
  }

  if (!data?.floorplans?.length) {
    return (
      <Box
        css={{
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Text textStyle="body-sm">No information found for this listing.</Text>
      </Box>
    );
  }

  const compFloorplans = data?.similarFloorplans?.floorplans ?? [];

  if (compFloorplans.length === 0) {
    return (
      <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Text textStyle="body-sm">We are unable to find any comparable listings nearby.</Text>
      </Box>
    );
  }

  const { lowestPriceIncluded, highestPriceIncluded } = data.similarFloorplans;
  const { formattedCompleteAddress, formattedKebobCase } = formatAddress(
    data.floorplans[0].address as unknown as Record<string, string>,
  );

  const contentComps = `${compFloorplans.length} comparable properties`;
  const contentSummary = `listed nearby for $${lowestPriceIncluded} - $${highestPriceIncluded}`;

  const generateAdditionalMarkers = () => {
    const formattedMarkers = compFloorplans.map((comp) => {
      return `color:${comp.active ? 'purple' : 'gray'}|size:tiny|${comp.geo.lat},${comp.geo.lon}`;
    });
    return formattedMarkers.join('&markers=');
  };

  const pmrHref = hasAnalyticsRentComps ? PMR_LANDING_PAGE : `${PMR_RESULTS_BASE}${formattedKebobCase}/`;
  const pmrUrl = new URL(pmrHref);
  if (address?.unit) pmrUrl.searchParams.set('unit', address.unit);

  const ctaText = hasAnalyticsRentComps ? 'Get rent comps' : 'Compare to your property';

  return (
    <Box css={{ flexGrow: 1 }} className="Comps">
      <Box css={{ display: 'grid', gap: '400', gridTemplateColumns: '50% 50%' }}>
        <Box
          css={{
            display: 'flex',
            justifyContent: 'center',
            alignContent: 'center',
            overflow: 'hidden',
            height: '125px',
            width: '100%',
            '& > img': { objectFit: 'cover' },
          }}
        >
          <StaticMap
            address={formattedCompleteAddress}
            additionalMarkers={generateAdditionalMarkers()}
            scale={2}
            size={isScreenTablet ? '350x125' : '180x125'}
            user={user}
            isScreenLgGTE={isScreenLgGTE}
          />
        </Box>
        <Box css={{ overflow: 'hidden', position: 'relative', height: '125px' }}>
          <Box css={{ textAlign: 'left', marginRight: '200' }}>
            <Text
              asChild
              css={{
                textStyle: { base: 'body-sm-bold', xl: 'body-bold' },
              }}
            >
              <p>{contentComps}</p>
            </Text>
            <Text asChild textStyle="body-sm">
              <p>{contentSummary}</p>
            </Text>
          </Box>
        </Box>
      </Box>

      <Box css={{ display: 'flex', justifyContent: 'center', marginTop: '400' }}>
        <TextButton asChild icon={<IconExternalOutline />} iconPosition="end">
          <a
            href={pmrUrl.toString()}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={`${ctaText} (opens in new tab)`}
          >
            {ctaText}
          </a>
        </TextButton>
      </Box>
    </Box>
  );
};

export default Comps;
